import type { ButtonHTMLAttributes, ReactNode } from 'react';

// Button — three variants:
//   primary → ore-filled CTA (`btn-primary` recipe)
//   ghost   → text-only button on transparent ground (`btn-ghost` recipe)
//   icon    → square icon-only button, hover lifts to surface-2
// The button forwards every standard HTML button attribute (type, disabled,
// onClick, etc.) and accepts an optional left-icon node — rendered inline
// before the children.
export type ButtonVariant = 'primary' | 'ghost' | 'icon';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  leftIcon?: ReactNode;
};

export function Button({
  variant = 'primary',
  leftIcon,
  className,
  children,
  type,
  ...rest
}: ButtonProps) {
  const variantClass =
    variant === 'primary' ? 'btn-primary' : variant === 'ghost' ? 'btn-ghost' : 'btn-icon';
  const composed = [variantClass, className].filter(Boolean).join(' ');

  // The icon variant has no global recipe (we keep recipes minimal). Provide
  // its styling inline to honour the rule "no hard-coded hex inside theme-aware
  // components" by reading from CSS variables only.
  const iconStyle =
    variant === 'icon'
      ? {
          display: 'inline-flex' as const,
          alignItems: 'center' as const,
          justifyContent: 'center' as const,
          width: '28px',
          height: '28px',
          borderRadius: '6px',
          background: 'transparent',
          color: 'var(--ink-secondary)',
          border: 'none',
          cursor: 'pointer',
          transition: 'background-color 120ms ease, color 120ms ease',
        }
      : undefined;

  return (
    <button type={type ?? 'button'} className={composed} style={iconStyle} {...rest}>
      {leftIcon ? <span className="inline-flex items-center">{leftIcon}</span> : null}
      {children}
    </button>
  );
}
